<template>
  <ValidationObserver
    ref="observer"
    v-slot="{ valid, dirty }"
  >
    <div class="content">
      <v-col
        cols="12"
      >
        <VTextField
          v-model="data.name[selectedLang]"
          :label="$t('forms.labels.name')"
          :rules="{ required: selectedLang === teamMainLang, min: 1, max: 200 }"
          :disabled="!isEditable"
          outlined
        />
      </v-col>


      <v-col
        md="4"
        sm="4"
        cols="12"
      >
        <VAutocomplete
          ref="autocompleteRefCat"
          v-model="data.category"
          :label="$t('forms.labels.category')"
          :items="displayedCategories"
          :loading="loading"
          :rules="{ required: true }"
          :disabled="!isEditable"
          :clearable="true"
          data-cy="CategorySelect"
          outlined
          @search="val => search(val, 'category')"
          @change="val => onChange(val, 'category')"
        >
          <template #withInfiniteScroll>
            <div v-intersect="endIntersectCat" />
          </template>
        </VAutocomplete>
      </v-col>
      <v-col
        md="4"
        sm="4"
        cols="12"
      >
        <VAutocomplete
          ref="autocompleteRefFR"
          v-model="data.frequency"
          :label="$t('forms.labels.frequency')"
          :items="displayedFrequencies"
          :loading="loading"
          :disabled="!isEditable"
          :clearable="true"
          data-cy="FrequencySelect"
          outlined
          @search="val => search(val, 'frequency')"
          @change="val => onChange(val, 'frequency')"
        >
          <template #withInfiniteScroll>
            <div v-intersect="endIntersectFR" />
          </template>
        </VAutocomplete>
      </v-col>
      <v-col
        md="4"
        sm="4"
        cols="12"
      >
        <VAutocomplete
          ref="autocompleteRefProc"
          v-model="data.procedure"
          :label="$t('forms.labels.procedure')"
          :items="displayedProcedures"
          :loading="loading"
          itemText="code"
          :disabled="!isEditable"
          :clearable="true"
          data-cy="ProcedureSelect"
          outlined
          @search="val => search(val, 'procedure')"
          @change="val => onChange(val, 'procedure')"
        >
          <template #withInfiniteScroll>
            <div v-intersect="endIntersectProc" />
          </template>
        </VAutocomplete>
      </v-col>
      <v-col
        md="6"
        sm="6"
        cols="12"
      >
        <VAutocomplete
          ref="refNetworkTypes"
          v-model="data.networkTypes"
          :label="$t('forms.labels.networkTypes')"
          :items="displayedNetworkTypes"
          multiple
          :loading="loading"
          :disabled="!isEditable"
          data-cy="NetworkSelect"
          outlined
        >
          <template #withInfiniteScroll>
            <div v-intersect="(_, __, isIntersecting) => endIntersect(isIntersecting, 'networkTypes')" />
          </template>
        </VAutocomplete>
      </v-col>

      <v-col
        md="6"
        sm="6"
        cols="12"
      >
        <VAutocomplete
          ref="refComponentTypes"
          v-model="data.componentTypes"
          :label="$t('forms.labels.componentTypes')"
          :items="displayedComponentTypes"
          multiple
          :loading="loading"
          :disabled="!isEditable"
          data-cy="ComponentSelect"
          outlined
        >
          <template #withInfiniteScroll>
            <div v-intersect="(_, __, isIntersecting) => endIntersect(isIntersecting, 'componentTypes')" />
          </template>
        </VAutocomplete>
      </v-col>

      <SlotScopeWatcher
        :value="dirty"
        @change="$emit('dirtyChanged', dirty)"
      />
      <SlotScopeWatcher
        :value="valid"
        @change="$emit('validChanged', valid)"
      />
    </div>
  </ValidationObserver>
</template>

<script>
import { ValidationObserver } from 'vee-validate';
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField.vue';
import SlotScopeWatcher from '@/components/shared/widgets/forms/validationHelper/SlotScopeWatcher';
import VAutocomplete from '@/components/shared/widgets/forms/VuetifyInputs/VAutocomplete.vue';
import { mapGetters } from 'vuex';
import { isEmpty } from 'lodash';

export default {
    components: {
        ValidationObserver,
        VTextField,
        SlotScopeWatcher,
        VAutocomplete,
    },
    props: {
        activity: {
            type: Object,
            default: () => ({}),
        },
        isEdition: {
            type: Boolean,
            default: false,
        },
        isEditable: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            loading: false,
            categories: [],
            frequencies: [],
            procedures: [],
            componentTypes: [],
            networkTypes: [],
            data: {
                name: {},
                category: null,
                frequency: null,
                procedure: null,
                componentTypes: null,
                networkTypes: null,
            },
            queryParamsCat: { perPage: 25 },
            paginationCat: {},
            PaginationFR: {},
            queryParamsFR: { perPage: 25 },
            paginationProc: {},
            queryParamsProc: { perPage: 25 },
            pagination: {},
            queryParams: { page: 1, perpage: 25, search: '' },
            networksTypesPagination: {},
            componentTypesPagination: {},
            networksTypesQueryParams: { page: 1, perpage: 25, search: '' },
            componentTypesQueryParams: { page: 1, perpage: 25, search: '' },
        };
    },
    computed: {
        ...mapGetters([
            'userLang',
            'selectedLang',
            'teamMainLang',
        ]),
        displayedCategories() {
            return this.getDisplayedItem(this.categories);
        },
        displayedFrequencies() {
            return this.getDisplayedItem(this.frequencies);
        },
        displayedComponentTypes() {
            return this.getDisplayedItem(this.componentTypes);
        },
        displayedNetworkTypes() {
            return this.getDisplayedItem(this.networkTypes);
        },
        displayedProcedures() {
            return this.procedures.map(({ id, name, code }) => ({
                id,
                name: name[this.selectedLang] || name[this.mainLang],
                code,
            }));
        },

    },
    watch: {
        activity: {
            handler() {
                if (this.isEdition) {
                    this.data = this.activity;
                }
            },
            immediate: true,
        },
        data: {
            handler() {
                this.$emit('sendData', this.data);
            },
            deep: true,
        },
    },
    async created() {
        this.loading = true;
        await this.getCategories();
        await this.getFrequencies();
        await this.getProcedures();
        await this.getComponentTypes();
        await this.getNetworkTypes();
        this.loading = false;
    },
    methods: {
        async getCategories() {
            if (this.isEdition) {
                const { data } = await this.$rq.getMaintenanceScheduleCategories({ pagination: false });
                this.categories = data.categories;
            } else if (isEmpty(this.paginationCat) || (this.paginationCat?.currentPage !== this.queryParamsCat?.page)) {
                const { data } = await this.$rq.getMaintenanceScheduleCategories(this.queryParamsCat);
                this.categories = [...this.categories, ...data.categories];
                this.paginationCat = data.pagination;
            }
        },
        async getFrequencies() {
            if (this.isEdition) {
                const { data } = await this.$rq.getFrequencies({ pagination: false });
                this.frequencies = data.frequencies;
            } else if (isEmpty(this.PaginationFR) || (this.PaginationFR?.currentPage !== this.queryParamsFR?.page)) {
                const { data } = await this.$rq.getFrequencies(this.queryParamsFR);
                this.frequencies = [...this.frequencies, ...data.frequencies];
                this.PaginationFR = data.pagination;
            }
        },
        async getProcedures() {
            if (this.isEdition) {
                const { data } = await this.$rq.getProcedures({ pagination: false });
                this.procedures = data.procedures;
            } else if (isEmpty(this.paginationProc) || (this.paginationProc?.currentPage !== this.paginationProc?.page)) {
                const { data } = await this.$rq.getProcedures(this.queryParamsProc);
                this.procedures = [...this.procedures, ...data.procedures];
                this.paginationProc = data.pagination;
            }
        },
        async getComponentTypes() {
            if (this.isEdition) {
                const { data } = await this.$rq.getComponentTypes({ pagination: false });
                this.componentTypes = data.componentTypes;
            } else if (this.componentTypesPagination?.currentPage !== this.componentTypesQueryParams?.page
            && this.componentTypesPagination?.totalPageCount
                ? this.componentTypesPagination?.totalPageCount >= this.componentTypesQueryParams?.page : true) {
                const { data } = await this.$rq.getComponentTypes(this.componentTypesQueryParams);
                this.componentTypes = [...data.componentTypes, ...this.componentTypes];
                this.componentTypesPagination = data.pagination;
            }
        },
        async getNetworkTypes() {
            if (this.isEdition) {
                const { data } = await this.$rq.getNetworkTypes({ pagination: false });
                this.networkTypes = data.networkTypes;
            } else if (this.networksTypesPagination?.currentPage !== this.networksTypesQueryParams?.page
            && this.networksTypesPagination?.totalPageCount
                ? this.networksTypesPagination?.totalPageCount >= this.networksTypesQueryParams?.page : true) {
                const { data } = await this.$rq.getNetworkTypes(this.networksTypesQueryParams);
                this.networkTypes = [...data.networkTypes, ...this.networkTypes];
                this.networksTypesPagination = data.pagination;
            }
        },
        getDisplayedItem(item) {
            return item?.map(({ id, name }) => ({
                id,
                name: name[this.selectedLang] ? name[this.selectedLang] : name[this.teamMainLang],
            }));
        },
        async search(search, type) {
            if (type === 'category') {
                const { data } = await this.$rq.getMaintenanceScheduleCategories({ search, pagination: false });
                this.categories = data.categories;
            } else if (type === 'frequency') {
                const { data } = await this.$rq.getFrequencies({ search, pagination: false });
                this.frequencies = data.frequencies;
            } else if (type === 'procedure') {
                const { data } = await this.$rq.getProcedures({ search, pagination: false });
                this.procedures = data.procedures;
            }
        },

        async onChange(val, type) {
            // when change is clear, reset the vselect list
            if (!val) {
                if (type === 'category') {
                    this.categories = [];
                    this.queryParamsCat = {};
                    this.paginationCat = {};
                    await this.getCategories();
                } else if (type === 'frequency') {
                    this.frequencies = [];
                    this.queryParamsFR = {};
                    this.paginationFR = {};
                    await this.getFrequencies();
                } else if (type === 'procedure') {
                    this.procedures = [];
                    this.queryParamsProc = {};
                    this.paginationProc = {};
                    await this.getProcedures();
                }
            }
        },

        async endIntersectFR(_, __, isIntersecting) {
            const condition = this.PaginationFR.currentPage < this.PaginationFR.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParamsFR.page = this.paginationFR.currentPage + 1;
                await this.getFrequencies();
                // access "v-autocomplete" ref
                const ref = this.$refs?.autocompleteRefFR?.$refs?.autocomplete;
                if (ref?.onScroll) {
                // workaround to refresh list after adding new items.
                    ref.onScroll();
                }
            }
        },
        async endIntersectCat(_, __, isIntersecting) {
            const condition = this.paginationCat.currentPage < this.paginationCat.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParamsCat.page = this.paginationCat.currentPage + 1;
                await this.getCategories();
                // access "v-autocomplete" ref
                const ref = this.$refs?.autocompleteRefCat?.$refs?.autocomplete;
                if (ref?.onScroll) {
                // workaround to refresh list after adding new items.
                    ref.onScroll();
                }
            }
        },
        async endIntersectProc(_, __, isIntersecting) {
            const condition = this.paginationProc.currentPage < this.paginationProc.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParamsProc.page = this.paginationProc.currentPage + 1;
                await this.getProcedures();
                // access "v-autocomplete" ref
                const ref = this.$refs?.autocompleteRefProc?.$refs?.autocomplete;
                if (ref?.onScroll) {
                // workaround to refresh list after adding new items.
                    ref.onScroll();
                }
            }
        },
        async endIntersectComponentTypes(isIntersecting) {
            const condition = this.componentTypesPagination?.currentPage < this.componentTypesPagination?.totalPageCount;
            if (isIntersecting && condition) {
                this.componentTypesQueryParams.page = this.componentTypesPagination.currentPage + 1;
                await this.getComponentTypes();
                const ref = this.$refs?.refComponentTypes?.$refs?.autocomplete;
                if (ref?.onScroll) {
                    // workaround to refresh list after adding new items.
                    ref.onScroll();
                }
            }
        },
        async endIntersectNetworkTypes(isIntersecting) {
            const condition = this.networksTypesPagination?.currentPage < this.networksTypesPagination?.totalPageCount;
            if (isIntersecting && condition) {
                this.networksTypesQueryParams.page = this.networksTypesPagination.currentPage + 1;
                await this.getNetworkTypes();
                const ref = this.$refs?.refNetworkTypes?.$refs?.autocomplete;
                if (ref?.onScroll) {
                    // workaround to refresh list after adding new items.
                    ref.onScroll();
                }
            }
        },
        async endIntersect(isIntersecting, field) {
            switch (field) {
                case 'componentTypes':
                    await this.endIntersectComponentTypes(isIntersecting);
                    break;
                case 'networkTypes':
                    await this.endIntersectNetworkTypes(isIntersecting);
                    break;
                default:
            }
        },
    },
};
</script>
<style scoped lang="scss">
  .content {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
  }
</style>
